import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { api, apiErrorCode } from "@/lib/api";

type Msg = { role: "user" | "agent"; content: string };
const QUICK = ["Book Appointment", "Get a Quote", "Reschedule", "Cancel", "Request Callback", "Talk to a Person"];

// The agent answers in plain text, and one of those answers is the whole point of the chat: the
// booking link. As text it sat there uncopiable, and a long one ran out past the edge of its
// bubble. Links are rendered as links — built as elements, never as HTML, so nothing the agent
// says can inject markup. Trailing punctuation stays out of the href. No lookbehind: a regex
// literal an older browser cannot parse takes the whole widget down with it.
const LINK = /(https?:\/\/[^\s<]+[^\s<.,:;"')\]])/g;

const Linked: React.FC<{ text: string; own: boolean }> = ({ text, own }) => (
  <>
    {(text || "").split(LINK).map((part, i) =>
      i % 2 === 1 ? (
        <a key={i} href={part} target="_blank" rel="noreferrer"
          className={own ? "underline" : "font-medium text-blue-600 underline"}>
          {part}
        </a>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      ))}
  </>
);

export const ChatWidget: React.FC<{ organizationId?: string }> = ({ organizationId }) => {
  const [open, setOpen] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [chatId, setChatId] = useState<string>("");
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [biz, setBiz] = useState("our team");
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { api.chatConfig().then((c) => setConfigured(c.configured)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  // End the chat when the visitor leaves the page: Retell only runs the agent's post-chat analysis
  // (the name, email and phone it extracts) on an ended chat, and it never ends one by itself.
  // Chats that are simply abandoned are ended by the server after sitting idle.
  useEffect(() => {
    if (!chatId) return;
    const end = () => api.chatEnd(chatId);
    window.addEventListener("pagehide", end);
    return () => window.removeEventListener("pagehide", end);
  }, [chatId]);

  const startChat = async () => {
    const started = await api.chatStart(organizationId);
    setChatId(started.chat_id);
    setBiz(started.business_name || "our team");
    return started;
  };

  const ensureChat = async (): Promise<string> => {
    if (chatId) return chatId;
    const { chat_id, business_name } = await startChat();
    // Prepend rather than replace, so a message sent before the chat existed stays on screen.
    setMsgs((m) => [{ role: "agent", content: `Hi! I'm the ${business_name || "team"} assistant. How can I help you today?` }, ...m]);
    return chat_id;
  };

  const openPanel = async () => {
    setOpen(true);
    if (!chatId && configured) { try { await ensureChat(); } catch { setMsgs([{ role: "agent", content: "Sorry, chat is unavailable right now." }]); } }
  };

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content }]);
    setBusy(true);
    try {
      const id = await ensureChat();
      let reply: Awaited<ReturnType<typeof api.chatMessage>>;
      try {
        reply = await api.chatMessage(id, content);
      } catch (e) {
        if (apiErrorCode(e) !== "chat_ended") throw e;
        // The chat was ended after sitting idle; carry on in a new one without clearing the screen.
        reply = await api.chatMessage((await startChat()).chat_id, content);
      }
      const { messages } = reply;
      setMsgs((m) => [...m, ...messages.map((x) => ({ role: "agent" as const, content: x.content }))]);
    } catch {
      setMsgs((m) => [...m, { role: "agent", content: "I hit a snag — please try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  if (!configured) return null;

  return (
    <>
      {!open && (
        <button onClick={openPanel} data-testid="chat-widget-open"
          className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-transform hover:scale-105">
          <MessageCircle className="h-6 w-6" />
        </button>
      )}
      {open && (
        <div data-testid="chat-widget-panel"
          className="fixed bottom-6 right-6 z-50 flex h-[540px] w-[370px] max-w-[92vw] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between bg-blue-600 px-4 py-3 text-white">
            <div className="text-sm font-semibold">Chat with {biz}</div>
            <button onClick={() => setOpen(false)} data-testid="chat-widget-close"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex-1 space-y-2 overflow-y-auto bg-slate-50 p-3" data-testid="chat-widget-messages">
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-blue-600 text-white" : "bg-white text-slate-800 shadow-sm"}`}>
                  <Linked text={m.content} own={m.role === "user"} />
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="rounded-2xl bg-white px-3 py-2 text-slate-400 shadow-sm"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
            <div ref={endRef} />
          </div>
          <div className="flex flex-wrap gap-1 border-t border-slate-100 bg-white px-2 py-2">
            {QUICK.map((q) => (
              <button key={q} onClick={() => send(q)} disabled={busy} data-testid={`chat-quick-${q.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-600 transition-colors hover:bg-slate-50">
                {q}
              </button>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 border-t border-slate-100 p-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type a message…"
              data-testid="chat-widget-input" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="chat-widget-send"
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white disabled:opacity-40">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
